/**
 * Public Accuplacer test login.
 *
 * Student arrives with a Test ID (e.g. ACP-4F2X) + password issued by the
 * admin (in-person or via email). On success we store the attempt
 * credentials in localStorage and route to /accuplacer/test/runner.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, KeyRound, ShieldAlert } from "lucide-react";

const STORAGE_KEY = "hccbc_accuplacer_attempt_v1";

function fmtError(err, fallback = "Login failed.") {
    const d = err?.response?.data?.detail;
    if (!d) return fallback;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || "Invalid input").join("; ");
    return d.msg || JSON.stringify(d);
}

export default function AccuplacerTestLogin() {
    const navigate = useNavigate();
    const [testId, setTestId] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e?.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/accuplacer/test-login", {
                test_id: testId.trim().toUpperCase(),
                password: password,
            });
            // Persist the attempt token so a tab refresh doesn't lose it.
            // Reading-bank questions are also stashed because re-fetching after
            // refresh works but we save the extra round-trip on first paint.
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                attempt_id: data.attempt_id,
                attempt_token: data.attempt_token,
                student_name: data.student_name,
            }));
            toast.success(`Welcome ${data.student_name?.split(" ")[0] || ""}. The test is opening in a new window…`);

            // Open the test in a separate browser window (not a tab) so the
            // student can't accidentally close it, switch tabs, or peek at
            // other sites. We size it to fill the screen and strip toolbars.
            const w = Math.max(window.screen.availWidth - 40, 1024);
            const h = Math.max(window.screen.availHeight - 40, 700);
            const features = [
                `width=${w}`,
                `height=${h}`,
                "left=20",
                "top=20",
                "toolbar=no",
                "location=no",
                "menubar=no",
                "status=no",
                "directories=no",
                "noopener=no",
                "noreferrer=no",
            ].join(",");
            const popup = window.open("/accuplacer/test/runner", "accuplacer_test", features);
            if (!popup) {
                // Pop-up was blocked. Fall back to in-tab navigation so the
                // student isn't stranded.
                toast.warning("Pop-up blocked — opening the test in this tab instead. Allow pop-ups for a proper full-window experience.");
                navigate("/accuplacer/test/runner", { state: { initial: data } });
                return;
            }
            // Replace the login tab with a placeholder so the student can't
            // navigate back to it during the test.
            navigate("/accuplacer/test/window-open", { replace: true });
        } catch (err) {
            toast.error(fmtError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-[80vh] bg-[#0A2818] flex items-center justify-center px-6 py-16" data-testid="accuplacer-test-login">
            <Card className="max-w-md w-full p-8 bg-[#134B30] text-white border-[#C79520]/30">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C79520] mb-2 flex items-center gap-2">
                    <ShieldAlert className="h-3.5 w-3.5" />
                    Proctored HCC English Test
                </div>
                <h2 className="font-display text-2xl mb-1">Sign in to begin</h2>
                <p className="text-sm text-white/70 mb-6">
                    Enter the Test ID and password issued by your proctor. These
                    credentials are single-use and expire 48 hours after issue.
                </p>

                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <Label className="text-white/80">Test ID</Label>
                        <Input
                            value={testId}
                            onChange={(e) => setTestId(e.target.value)}
                            placeholder="ACP-XXXX"
                            required
                            className="bg-white/95 text-[#1A201D] font-mono tracking-wider text-base"
                            data-testid="test-login-id"
                        />
                    </div>
                    <div>
                        <Label className="text-white/80">Password</Label>
                        <Input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="bg-white/95 text-[#1A201D] font-mono"
                            data-testid="test-login-password"
                        />
                    </div>
                    <Button
                        type="submit"
                        disabled={busy || !testId || !password}
                        className="w-full bg-[#C79520] hover:bg-[#B17E0F] text-[#0A2818] font-semibold gap-2"
                        data-testid="test-login-submit"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                        Begin test
                    </Button>
                </form>

                <p className="text-[11px] text-white/50 mt-6 leading-relaxed">
                    By signing in you agree to take this test honestly, without
                    consulting any external resources, persons, or AI tools. Tab
                    switches and prohibited activity are recorded and may invalidate
                    your result.
                </p>
            </Card>
        </div>
    );
}
